import { createRouter, createWebHistory } from 'vue-router'
import NProgress from 'nprogress'

import authRoutes from './authRoutes'
import commonRoutes from './commonRoutes'
import platformRoutes from './platformRoutes'
import clientFundRoutes from './clientFundRoutes'
import referrerRoutes from './referrerRoutes'
import assistantRoutes from './assistantRoutes'
import devRoutes from './devRoutes'
import TokenService from '@/services/user/token'
import UserService from '@/services/user/user'

import { useGlobalStore } from '@/stores/global'
import { useUserStore } from '@/stores/user'

import type { RouteRecord, RouteRecordRaw } from 'vue-router'
import type { SessionRole } from '@/types/services/user'

const defaultRoutes = [...commonRoutes, ...authRoutes]

// Enable development only routes (for component previews)
const isDev = import.meta.env.DEV
if (isDev) {
  defaultRoutes.push(...devRoutes)
}

const router = createRouter({
  history: createWebHistory('/'),
  routes: defaultRoutes,
})

const NO_PERMISSION_ROUTE_NAME = 'NoPermissionView'

let isReady = false

export const markReady = () => {
  isReady = true
}

const checkIfCanAccess = (route: RouteRecordRaw | RouteRecord): boolean => {
  const userStore = useUserStore()

  let hasPackage = true
  let hasPermission = true
  if (
    route.meta?.packages != null &&
    (route.meta.packages as number[]).length
  ) {
    hasPackage = userStore.hasPackage(route.meta.packages as number[])
  }
  if (route.meta?.permission != null) {
    hasPermission = userStore.hasPermission(route.meta.permission as number)
  }
  return hasPackage && hasPermission
}

const findRouteWithAccess = (parent: RouteRecordRaw): string => {
  let routeName = NO_PERMISSION_ROUTE_NAME
  const children = parent.children
  if (children) {
    for (let i = 0; i < children.length; i++) {
      const child = children[i]
      if (child.name !== 'AccountSettingsView' && checkIfCanAccess(child)) {
        if (child.children && child.children.length) {
          routeName = findRouteWithAccess(child)
        } else if (child.name) {
          routeName = child.name as string
        }
        break
      }
    }
  }
  return routeName
}

/**
 * Setup routes based on current user session role
 */
let firstRouteWithAccess: string | { name: string } = '/'
const setupRoutes = (role: SessionRole): void => {
  router.clearRoutes()

  const routes = [...defaultRoutes]

  if (role == 'client') {
    routes.push(...clientFundRoutes)
  } else if (role == 'referral') {
    routes.push(...referrerRoutes)
  } else if (role == 'assistant') {
    routes.push(...assistantRoutes)
  } else {
    routes.push(...platformRoutes)

    firstRouteWithAccess = {
      name: findRouteWithAccess(platformRoutes[0]),
    }
    platformRoutes[0].redirect = firstRouteWithAccess
  }

  routes.forEach((route) => router.addRoute(route))
}

/**
 * Checks if the current route is accessible with current permissions/packages
 * Returns the route to redirect to if access is denied, or null if access is granted
 */
const getRedirectRouteIfNoAccess = (route: {
  matched: RouteRecord[]
}): string | { name: string } | null => {
  const canAccess = route.matched.every((r) => checkIfCanAccess(r))

  if (!canAccess) {
    return firstRouteWithAccess
  }

  return null
}

router.beforeEach(async (to, from, next) => {
  const globalStore = useGlobalStore()
  const userStore = useUserStore()

  NProgress.start()

  while (!isReady) {
    await new Promise((resolve) => setTimeout(resolve, 50))
  }

  const onlyWhenLoggedOut = to.matched.some(
    (record) => record.meta.onlyWhenLoggedOut
  )

  /**
   * Redirect to SignIn if not authenticated
   */
  if (!userStore.session_started && !onlyWhenLoggedOut) {
    return next({ name: 'SignIn' })
  }

  /**
   * If session started but user has multiple accounts and no session was resolved,
   * redirect to AccountSelection
   */
  if (
    TokenService.getAuthToken() &&
    !userStore.session_started &&
    userStore.hasMultipleAccounts &&
    to.name !== 'AccountSelection' &&
    to.name !== 'SignIn'
  ) {
    return next({ name: 'AccountSelection' })
  }

  // This goes through the matched routes from last to first, finding the closest route with a title.
  // e.g. if we have /some/deep/nested/route and /some, /deep, and /nested have titles, nested's will be chosen.
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.name)

  // If a route with a title was found, set the document (page) title to that value.
  if (nearestWithTitle) {
    document.title = `${nearestWithTitle.meta.name} | Crypkit`
  }

  /**
   * Set to store active view name
   */
  let name = 'Crypkit'
  for (let i = to.matched.length - 1; i >= 0; i--) {
    const item = to.matched[i]
    if (item.meta && item.meta.name) {
      name = item.meta.name as string
      break
    }
  }
  globalStore.activeViewName = name

  /**
   * Log previous user out if required
   */
  const logoutBeforeVisit = to.matched.some(
    (record) => record.meta.logoutBeforeVisit
  )
  if (logoutBeforeVisit && userStore.session_started) {
    // Log out previously logged user (without redirect)
    await UserService.logout('', false)
    userStore.session_started = false
  }

  /**
   * Check if "to" is "no permission" route and user has access to another routes (e.g. permission was granted and user refresh)
   */
  const checkRouteName =
    typeof firstRouteWithAccess === 'string'
      ? firstRouteWithAccess
      : firstRouteWithAccess.name

  if (
    to.name === NO_PERMISSION_ROUTE_NAME &&
    checkRouteName !== NO_PERMISSION_ROUTE_NAME
  ) {
    return next(firstRouteWithAccess)
  }

  /**
   * Check if user has required packages or permissions to access the route
   */
  const redirectTo = getRedirectRouteIfNoAccess(to)

  if (redirectTo) {
    return next(redirectTo)
  }

  /**
   * Do not allow user to visit login page or register page if they are logged in
   */
  if (userStore.session_started && onlyWhenLoggedOut) {
    return next('/')
  }

  return next()
})

router.beforeResolve((to, from, next) => {
  NProgress.done()
  next()
})

export {
  setupRoutes,
  checkIfCanAccess,
  firstRouteWithAccess,
  getRedirectRouteIfNoAccess,
}

export default router
